import React, { useState, useEffect } from 'react';
import { ChevronRight, Building2, MapPin, BarChart3, TrendingUp, AlertCircle, FileText, Download, Search, Filter, RefreshCw } from 'lucide-react';

// ==========================================
// TYPESCRIPT TYPES & INTERFACES
// ==========================================

type HierarchyLevel = 'state' | 'ce_territory' | 'circle' | 'district' | 'division' | 'sub_division' | 'mandal';

interface HierarchicalSummary {
  total_works: number;
  total_estimate_cost: number;
  total_ras_amount: number;
  total_expenditure: number;
  total_length_sanctioned: number;
  total_length_completed: number;
  total_bills_pending: number;
  total_work_not_recorded: number;
  financial_progress_percentage: number;
  physical_progress_percentage: number;
  avg_estimate_cost: number;
  stage_wise_breakdown: {
    [stage: string]: {
      count: number;
      total_cost: number;
      total_expenditure: number;
    };
  };
  grant_wise_breakdown: {
    [grant: string]: {
      count: number;
      total_cost: number;
      total_expenditure: number;
    };
  };
}

interface EntityDetails {
  id: number;
  code: string;
  name: string;
  type: HierarchyLevel;
}

interface HierarchicalWorkResponse {
  summary: HierarchicalSummary;
  works?: Work[];
  children?: Array<{
    id: number;
    code: string;
    name: string;
    works_summary: HierarchicalSummary;
  }>;
  total?: number;
  totalPages?: number;
  currentPage?: number;
  entity_details: EntityDetails;
}

interface Work {
  work_id: number;
  sno: number;
  work_name: string;
  estimate_cost: number;
  agency_name: string;
  abstract_stage_of_work: string;
  oas_sanction_date: string;
  completion_date_set?: string;
  district?: { name: string };
  mandal?: { name: string };
  contractor?: { contractor_name: string };
}

interface ConsolidatedDashboard {
  overview: HierarchicalSummary;
  performance_metrics: {
    top_performers: Array<{
      entity: any;
      summary: HierarchicalSummary;
      performance_score: number;
    }>;
    bottom_performers: Array<{
      entity: any;
      summary: HierarchicalSummary;
      performance_score: number;
    }>;
    average_metrics: {
      avg_works_per_entity: number;
      avg_cost_per_entity: number;
      avg_completion_rate: number;
      avg_financial_progress: number;
    };
  };
  stage_analysis: {
    [stage: string]: {
      count: number;
      percentage: number;
      total_cost: number;
      avg_cost: number;
    };
  };
  trend_analysis: {
    monthly_sanctions: Array<{
      month: string;
      works_count: number;
      total_cost: number;
    }>;
  };
}

interface BreadcrumbItem {
  type: HierarchyLevel;
  name: string;
  id?: number;
  current?: boolean;
}

// ==========================================
// API SERVICE LAYER
// ==========================================

class HierarchicalWorksAPI {
  private baseURL = 'http://localhost:3000/api/v1/works/hierarchical';


  private async fetchAPI(endpoint: string, options: RequestInit = {}): Promise<any> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // State level operations
  async getStateDashboard(): Promise<ConsolidatedDashboard> {
    return this.fetchAPI('/state/dashboard');
  }

  async getStateOverview(): Promise<any> {
    return this.fetchAPI('/state/overview');
  }

  // Generic hierarchical operations
  async getWorksByLevel(
    level: Exclude<HierarchyLevel, 'state'>, 
    entityId: number, 
    options: {
      includeWorks?: boolean;
      includeChildren?: boolean;
      stage?: string;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<HierarchicalWorkResponse> {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    return this.fetchAPI(`/${level}/${entityId}?${params}`);
  }

  async getDashboardByLevel(
    level: Exclude<HierarchyLevel, 'state'>, 
    entityId: number
  ): Promise<ConsolidatedDashboard> {
    return this.fetchAPI(`/${level}/${entityId}/dashboard`);
  }

  // Search operations
  async searchWorks(searchParams: {
    searchTerm?: string;
    stage?: string;
    level?: HierarchyLevel;
    entityId?: number;
    page?: number;
    limit?: number;
  }): Promise<any> {
    return this.fetchAPI('/search', {
      method: 'POST',
      body: JSON.stringify(searchParams),
    });
  }

  // Comparison operations
  async getComparison(
    level: 'ce_territory' | 'circle' | 'district' | 'division' | 'sub_division',
    parentId?: number
  ): Promise<any> {
    const params = parentId ? `?parentId=${parentId}` : '';
    return this.fetchAPI(`/comparison/${level}${params}`);
  }

  // Navigation operations
  async getBreadcrumb(level: HierarchyLevel, entityId: number): Promise<{
    breadcrumb: BreadcrumbItem[];
    current_level: HierarchyLevel;
    current_entity: EntityDetails;
  }> {
    return this.fetchAPI(`/navigation/breadcrumb/${level}/${entityId}`);
  }

  async getChildren(
    level: 'ce_territory' | 'circle' | 'district' | 'division' | 'sub_division',
    entityId: number
  ): Promise<any> {
    return this.fetchAPI(`/navigation/children/${level}/${entityId}`);
  }

  // Analytics
  async getAnalytics(level: HierarchyLevel, entityId: number): Promise<any> {
    return this.fetchAPI(`/analytics/${level}/${entityId}`);
  }

  async getInsights(level: HierarchyLevel, entityId: number): Promise<any> {
    return this.fetchAPI(`/insights/${level}/${entityId}`);
  }

  // Export operations
  async exportData(
    level: HierarchyLevel, 
    entityId: number, 
    format: 'excel' | 'csv' | 'pdf' = 'excel'
  ): Promise<any> {
    return this.fetchAPI(`/export/${level}/${entityId}?format=${format}`);
  }
}

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

const formatCurrency = (amount: number | null | undefined): string => {
    if (amount == null) { // Handles both null and undefined
      return '₹0'; // Fallback value, adjust as needed
    }
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else {
      return `₹${amount.toLocaleString('en-IN')}`; // Use 'en-IN' for Indian number formatting
    }
  };

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

const getStageColor = (stage: string): string => {
  const stageColors: { [key: string]: string } = {
    'Completed': 'text-green-600 bg-green-100',
    'In Progress': 'text-blue-600 bg-blue-100',
    'Not Started': 'text-gray-600 bg-gray-100',
    'Suspended': 'text-red-600 bg-red-100',
    'Tendering': 'text-yellow-600 bg-yellow-100',
  };
  return stageColors[stage] || 'text-gray-600 bg-gray-100';
};

const getHierarchyIcon = (level: HierarchyLevel): React.ReactNode => {
  const iconProps = { size: 16, className: "inline" };
  
  switch (level) {
    case 'state':
      return <Building2 {...iconProps} />;
    case 'ce_territory':
      return <Building2 {...iconProps} />;
    case 'circle':
      return <MapPin {...iconProps} />;
    case 'district':
      return <MapPin {...iconProps} />;
    case 'division':
      return <MapPin {...iconProps} />;
    case 'sub_division':
      return <MapPin {...iconProps} />;
    case 'mandal':
      return <MapPin {...iconProps} />;
    default:
      return <Building2 {...iconProps} />;
  }
};

// ==========================================
// REACT COMPONENTS
// ==========================================

// Breadcrumb Navigation Component
const BreadcrumbNavigation: React.FC<{
  breadcrumb: BreadcrumbItem[];
  onNavigate: (level: HierarchyLevel, id?: number) => void;
}> = ({ breadcrumb, onNavigate }) => {
  return (
    <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
      {breadcrumb.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChevronRight size={16} className="text-gray-400" />}
          <button
            onClick={() => onNavigate(item.type, item.id)}
            className={`flex items-center space-x-1 hover:text-blue-600 ${
              item.current ? 'text-blue-600 font-medium' : ''
            }`}
            disabled={item.current}
          >
            {getHierarchyIcon(item.type)}
            <span>{item.name}</span>
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
};

// Summary Cards Component
const SummaryCards: React.FC<{ summary: HierarchicalSummary }> = ({ summary }) => {
  const cards = [
    {
      title: 'Total Works',
      value: summary.total_works.toLocaleString(),
      icon: <FileText className="w-8 h-8 text-blue-600" />,
      color: 'border-blue-200 bg-blue-50'
    },
    {
      title: 'Total Investment',
      value: formatCurrency(summary.total_estimate_cost),
      icon: <TrendingUp className="w-8 h-8 text-green-600" />,
      color: 'border-green-200 bg-green-50'
    },
    {
      title: 'Physical Progress',
      value: formatPercentage(summary.physical_progress_percentage),
      icon: <BarChart3 className="w-8 h-8 text-purple-600" />,
      color: 'border-purple-200 bg-purple-50'
    },
    {
      title: 'Financial Progress',
      value: formatPercentage(summary.financial_progress_percentage),
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      color: 'border-orange-200 bg-orange-50'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {cards.map((card, index) => (
        <div key={index} className={`p-6 rounded-lg border-2 ${card.color}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{card.title}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
            </div>
            {card.icon}
          </div>
        </div>
      ))}
    </div>
  );
};

// Stage Analysis Component
const StageAnalysis: React.FC<{
  stageAnalysis: { [stage: string]: { count: number; percentage: number; total_cost: number } };
}> = ({ stageAnalysis }) => {
  return (
    <div className="bg-white rounded-lg border p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Works by Stage</h3>
      <div className="space-y-4">
        {Object.entries(stageAnalysis).map(([stage, data]) => (
          <div key={stage} className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStageColor(stage)}`}>
                {stage}
              </span>
              <span className="text-sm text-gray-600">
                {data.count} works ({formatPercentage(data.percentage)})
              </span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(data.total_cost)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Children Entities Grid Component
const ChildrenEntitiesGrid: React.FC<{
  children: Array<{
    id: number;
    code: string;
    name: string;
    works_summary: HierarchicalSummary;
  }>;
  childLevel: HierarchyLevel;
  onDrillDown: (level: HierarchyLevel, id: number) => void;
}> = ({ children, childLevel, onDrillDown }) => {
  return (
    <div className="bg-white rounded-lg border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {childLevel.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}s
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {children.map((child) => (
          <div
            key={child.id}
            onClick={() => onDrillDown(childLevel, child.id)}
            className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">{child.name}</h4>
              <ChevronRight size={16} className="text-gray-400" />
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Works: {child.works_summary.total_works}</div>
              <div>Investment: {formatCurrency(child.works_summary.total_estimate_cost)}</div>
              <div>Progress: {formatPercentage(child.works_summary.physical_progress_percentage)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Works List Component
const WorksList: React.FC<{
  works: Work[];
  total: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ works, total, currentPage, totalPages, onPageChange }) => {
  return (
    <div className="bg-white rounded-lg border">
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold text-gray-900">
          Works List ({total} total)
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                S.No
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Work Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estimate Cost
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agency
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {works.map((work) => (
              <tr key={work.work_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {work.sno}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="max-w-xs truncate" title={work.work_name}>
                    {work.work_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(work.estimate_cost)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(work.abstract_stage_of_work)}`}>
                    {work.abstract_stage_of_work}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="max-w-xs truncate" title={work.agency_name}>
                    {work.agency_name}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Search and Filter Component
const SearchAndFilter: React.FC<{
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onSearch: () => void;
  selectedStage: string;
  onStageChange: (stage: string) => void;
  onClearFilters: () => void;
}> = ({ searchTerm, onSearchChange, onSearch, selectedStage, onStageChange, onClearFilters }) => {
  const stages = ['All', 'Not Started', 'Tendering', 'Work Awarded', 'In Progress', 'Completed', 'Suspended'];

  return (
    <div className="bg-white rounded-lg border p-4 mb-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search works by name, agency, or proceedings number..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onSearch()}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <select
            value={selectedStage}
            onChange={(e) => onStageChange(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {stages.map(stage => (
              <option key={stage} value={stage === 'All' ? '' : stage}>
                {stage}
              </option>
            ))}
          </select>
          
          <button
            onClick={onSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Search size={16} />
            <span>Search</span>
          </button>
          
          <button
            onClick={onClearFilters}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <RefreshCw size={16} />
            <span>Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Action Bar Component
const ActionBar: React.FC<{
  currentLevel: HierarchyLevel;
  entityId?: number;
  onExport: (format: string) => void;
  onRefresh: () => void;
}> = ({ currentLevel, entityId, onExport, onRefresh }) => {
  return (
    <div className="flex justify-between items-center mb-6">
      <h1 className="text-2xl font-bold text-gray-900">
        {currentLevel === 'state' ? 'Telangana State' : `${currentLevel.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}`} Dashboard
      </h1>
      
      <div className="flex space-x-2">
        <button
          onClick={onRefresh}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Refresh</span>
        </button>
        
        <div className="relative group">
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2">
            <Download size={16} />
            <span>Export</span>
          </button>
          <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
            <button
              onClick={() => onExport('excel')}
              className="w-full text-left px-4 py-2 hover:bg-gray-50"
            >
              Export to Excel
            </button>
            <button
              onClick={() => onExport('csv')}
              className="w-full text-left px-4 py-2 hover:bg-gray-50"
            >
              Export to CSV
            </button>
            <button
              onClick={() => onExport('pdf')}
              className="w-full text-left px-4 py-2 hover:bg-gray-50"
            >
              Export to PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN APPLICATION COMPONENT
// ==========================================

const HierarchicalWorksApp: React.FC = () => {
  // State management
  const [currentLevel, setCurrentLevel] = useState<HierarchyLevel>('state');
  const [currentEntityId, setCurrentEntityId] = useState<number | undefined>();
  const [dashboard, setDashboard] = useState<ConsolidatedDashboard | null>(null);
  const [worksData, setWorksData] = useState<HierarchicalWorkResponse | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [showWorks, setShowWorks] = useState(false);
  const [worksPage, setWorksPage] = useState(1);

  // API instance
  const api = new HierarchicalWorksAPI();

  // Load data based on current level and entity
  const loadData = async (level: HierarchyLevel, entityId?: number, includeWorks = false) => {
    setLoading(true);
    setError(null);
    
    try {
      if (level === 'state') {
        const [dashboardData, overviewData] = await Promise.all([
          api.getStateDashboard(),
          api.getStateOverview()
        ]);
        setDashboard(dashboardData);
        setWorksData(overviewData);
        setBreadcrumb([{ type: 'state', name: 'Telangana State', current: true }]);
      } else if (entityId) {
        const [dashboardData, worksResponse] = await Promise.all([
          api.getDashboardByLevel(level, entityId),
          api.getWorksByLevel(level, entityId, {
            includeChildren: !includeWorks,
            includeWorks,
            stage: selectedStage || undefined,
            page: worksPage,
            limit: 50
          })
        ]);
        
        setDashboard(dashboardData);
        setWorksData(worksResponse);
        
        // Get breadcrumb navigation
        const breadcrumbData = await api.getBreadcrumb(level, entityId);
        setBreadcrumb(breadcrumbData.breadcrumb);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const handleNavigate = (level: HierarchyLevel, entityId?: number) => {
    setCurrentLevel(level);
    setCurrentEntityId(entityId);
    setShowWorks(false);
    setWorksPage(1);
    loadData(level, entityId);
  };

  const handleDrillDown = (level: HierarchyLevel, entityId: number) => {
    handleNavigate(level, entityId);
  };

  // Search handlers
  const handleSearch = async () => {
    if (!searchTerm && !selectedStage) {
      loadData(currentLevel, currentEntityId, showWorks);
      return;
    }

    setLoading(true);
    try {
      const searchResults = await api.searchWorks({
        searchTerm: searchTerm || undefined,
        stage: selectedStage || undefined,
        level: currentLevel !== 'state' ? currentLevel : undefined,
        entityId: currentEntityId,
        page: 1,
        limit: 50
      });
      
      setWorksData(searchResults);
      setShowWorks(true);
      setWorksPage(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStage('');
    setShowWorks(false);
    loadData(currentLevel, currentEntityId);
  };

  // Export handler
  const handleExport = async (format: string) => {
    try {
      if (currentLevel === 'state') {
        // Handle state-level export
        alert(`Exporting state data as ${format}...`);
      } else if (currentEntityId) {
        const exportData = await api.exportData(currentLevel, currentEntityId, format as any);
        alert(`Export initiated: ${exportData.filename}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  // Initialize app
  useEffect(() => {
    loadData('state');
  }, []);

  // Get child level for navigation
  const getChildLevel = (level: HierarchyLevel): HierarchyLevel | null => {
    const hierarchy: HierarchyLevel[] = ['state', 'ce_territory', 'circle', 'district', 'division', 'sub_division', 'mandal'];
    const currentIndex = hierarchy.indexOf(level);
    return currentIndex < hierarchy.length - 1 ? hierarchy[currentIndex + 1] : null;
  };

  // Render loading state
  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => loadData(currentLevel, currentEntityId)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const childLevel = getChildLevel(currentLevel);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Bar */}
        <ActionBar
          currentLevel={currentLevel}
          entityId={currentEntityId}
          onExport={handleExport}
          onRefresh={() => loadData(currentLevel, currentEntityId, showWorks)}
        />

        {/* Breadcrumb Navigation */}
        {breadcrumb.length > 1 && (
          <BreadcrumbNavigation
            breadcrumb={breadcrumb}
            onNavigate={handleNavigate}
          />
        )}

        {/* Search and Filter */}
        <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={handleSearch}
          selectedStage={selectedStage}
          onStageChange={setSelectedStage}
          onClearFilters={handleClearFilters}
        />

        {/* Loading Indicator */}
        {loading && (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        )}

        {/* Dashboard Content */}
        {dashboard && (
          <>
            {/* Summary Cards */}
            <SummaryCards summary={dashboard.overview} />

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Stage Analysis */}
              <div className="lg:col-span-1">
                <StageAnalysis stageAnalysis={dashboard.stage_analysis} />
                
                {/* Performance Metrics */}
                {dashboard.performance_metrics.top_performers.length > 0 && (
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performers</h3>
                    <div className="space-y-3">
                      {dashboard.performance_metrics.top_performers.slice(0, 5).map((performer, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{performer.entity.name}</p>
                            <p className="text-sm text-gray-600">
                              {performer.summary.total_works} works
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-green-600">
                              {performer.performance_score}%
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatCurrency(performer.summary.total_estimate_cost)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Children or Works */}
              <div className="lg:col-span-2">
                {/* Toggle between Children and Works view */}
                {currentLevel !== 'mandal' && !showWorks && (
                  <div className="mb-4 flex space-x-2">
                    <button
                      onClick={() => setShowWorks(false)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                    >
                      View {childLevel?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}s
                    </button>
                    <button
                      onClick={() => {
                        setShowWorks(true);
                        loadData(currentLevel, currentEntityId, true);
                      }}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      View Works
                    </button>
                  </div>
                )}

                {/* Children Entities Grid */}
                {!showWorks && worksData?.children && childLevel && (
                  <ChildrenEntitiesGrid
                    children={worksData.children}
                    childLevel={childLevel}
                    onDrillDown={handleDrillDown}
                  />
                )}

                {/* Works List */}
                {(showWorks || currentLevel === 'mandal') && worksData?.works && (
                  <WorksList
                    works={worksData.works}
                    total={worksData.total || 0}
                    currentPage={worksData.currentPage || 1}
                    totalPages={worksData.totalPages || 1}
                    onPageChange={(page) => {
                      setWorksPage(page);
                      loadData(currentLevel, currentEntityId, true);
                    }}
                  />
                )}

                {/* Empty State */}
                {!worksData?.children?.length && !worksData?.works?.length && !loading && (
                  <div className="bg-white rounded-lg border p-12 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
                    <p className="text-gray-600">
                      There are no {showWorks ? 'works' : 'entities'} to display at this level.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Trend Analysis Chart Placeholder */}
            {dashboard.trend_analysis.monthly_sanctions.length > 0 && (
              <div className="mt-8 bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Sanctions Trend</h3>
                <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">Chart visualization would go here</p>
                    <p className="text-sm text-gray-500">
                      Data available for {dashboard.trend_analysis.monthly_sanctions.length} months
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats Footer */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {formatPercentage(dashboard.performance_metrics.average_metrics.avg_completion_rate)}
                </div>
                <div className="text-sm text-gray-600">Avg Completion Rate</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(dashboard.performance_metrics.average_metrics.avg_cost_per_entity)}
                </div>
                <div className="text-sm text-gray-600">Avg Cost per Entity</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {dashboard.performance_metrics.average_metrics.avg_works_per_entity.toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">Avg Works per Entity</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {formatPercentage(dashboard.performance_metrics.average_metrics.avg_financial_progress)}
                </div>
                <div className="text-sm text-gray-600">Avg Financial Progress</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HierarchicalWorksApp;